import React, { useEffect, useState } from "react";
import {
  Button,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { Stage } from "../../lusca-search-api/generated-src";
import { generateAthenaConsoleUrl } from "../../helpers/logUtils";
import { InvestigationRule, ruleToAthenaQuery } from "./investigationUtils";
import "./HoverHint.scss";

/** Athena keeps query history for 45 days; after that a history deep link opens nothing. */
export const ATHENA_HISTORY_RETENTION_DAYS = 45;

interface InvestigationAthenaQueryProps {
  stage: Stage;
  /** One rule, or the OR branches of a merged investigation. */
  rule?: InvestigationRule | InvestigationRule[] | null;
  /** Athena execution that froze the population; enables a deep link to the exact query. */
  queryExecutionId?: string | null;
  /** When the population was frozen (ISO); gates the deep link on Athena's history retention. */
  frozenAt?: string | null;
  /** Injectable clock for tests. */
  now?: () => number;
}

/**
 * True when Athena can still be expected to hold the execution in its history. An unknown or
 * unparseable freeze time is treated as expired so the button never promises a query it may
 * not be able to show.
 */
export function athenaHistoryAvailable(
  frozenAt: string | null | undefined,
  now: number = Date.now()
): boolean {
  if (!frozenAt) {
    return false;
  }
  const frozen = Date.parse(frozenAt);
  if (Number.isNaN(frozen)) {
    return false;
  }
  return now - frozen < ATHENA_HISTORY_RETENTION_DAYS * 24 * 60 * 60 * 1000;
}

const COPIED_MS = 3000;

/**
 * Inline Athena actions for a population rule: copy the SQL, or open Athena. With the freezing
 * execution's id (and a freeze younger than Athena's history retention) the link opens that
 * query and its results from Athena history; otherwise the console cannot be pre-filled from a
 * URL, so the editor opens blank and the user pastes the copied SQL. Meant for the corner of a rule block.
 */
export const InvestigationAthenaQuery: React.FC<
  InvestigationAthenaQueryProps
> = ({ stage, rule, queryExecutionId, frozenAt, now = Date.now }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(t);
  }, [copied]);

  if (!rule?.length) {
    return null;
  }
  const deepLink =
    !!queryExecutionId && athenaHistoryAvailable(frozenAt, now());
  const copy = () => {
    navigator.clipboard
      .writeText(ruleToAthenaQuery(rule, stage))
      .then(() => setCopied(true))
      .catch((err) => console.error("Failed to copy:", err));
  };

  // Icon-only actions for a code-block style corner toolbar: the rule is the content, so its
  // tools stay small and out of the way (like the copy button on a code snippet).
  const openLabel = deepLink ? "Open query in Athena" : "Open Athena editor";
  return (
    <SpaceBetween direction="horizontal" size="xxs" alignItems="center">
      {copied && <StatusIndicator type="success">Copied</StatusIndicator>}
      <span className="lusca-hover-hint">
        <Button
          variant="icon"
          iconName="copy"
          ariaLabel="Copy Athena query"
          onClick={copy}
        />
        <span className="lusca-hover-hint__bubble" aria-hidden="true">
          Copy Athena query
        </span>
      </span>
      <span className="lusca-hover-hint">
        <span className="lusca-hover-hint__bubble" aria-hidden="true">
          {openLabel}
        </span>
        <Button
          variant="icon"
          iconName="external"
          ariaLabel={openLabel}
          href={generateAthenaConsoleUrl(
            stage,
            deepLink ? queryExecutionId : undefined
          )}
          target="_blank"
          rel="noopener noreferrer"
        />
      </span>
    </SpaceBetween>
  );
};

export default InvestigationAthenaQuery;
